"use client";

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

// Animation duration: 4 seconds (matches GIF duration)
const ANIMATION_DURATION = 4000;
const PRELOADER_KEY = "tavlo_preloader_shown";

/**
 * Premium Landing Page Preloader Component
 * Shows animated logo GIF on first visit per session
 */
export function LandingPagePreloader() {
  const [showPreloader, setShowPreloader] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Initialize and check sessionStorage
  useEffect(() => {
    setMounted(true);

    // Check if preloader was already shown
    const hasShown =
      typeof window !== "undefined" &&
      sessionStorage.getItem(PRELOADER_KEY) === "true";

    const landingContent = document.getElementById("landing-page-content");

    if (!hasShown) {
      setShowPreloader(true);
      // Keep landing page hidden (CSS handles this with display: none)
    } else {
      // Already shown - show content immediately
      if (landingContent) {
        landingContent.classList.add("loaded");
      }
    }
  }, []);

  // Handle preloader timing - exit after exactly 4 seconds
  useEffect(() => {
    if (!showPreloader) return;

    // Exit after animation duration (4 seconds)
    const timer = setTimeout(() => {
      setIsExiting(true);
      // Show landing page content (removes display: none)
      const landingContent = document.getElementById("landing-page-content");
      if (landingContent) {
        landingContent.classList.add("loaded");
      }
    }, ANIMATION_DURATION);

    return () => {
      clearTimeout(timer);
    };
  }, [showPreloader]);

  // Mark as shown and cleanup when animation completes
  const handleExitComplete = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(PRELOADER_KEY, "true");
    }
    setShowPreloader(false);
    // Ensure content is visible
    const landingContent = document.getElementById("landing-page-content");
    if (landingContent && !landingContent.classList.contains("loaded")) {
      landingContent.classList.add("loaded");
    }
  }, []);

  // Don't render during SSR or if shouldn't show
  if (!mounted || !showPreloader) {
    return null;
  }

  const logoSrc = "/logo-animations/dark.gif";
  const bgColor = "bg-black";

  return (
    <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
      {!isExiting && (
        <motion.div
          key="preloader"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className={`fixed inset-0 z-[9999] flex items-center justify-center ${bgColor}`}
          style={{ willChange: "opacity" }}
          role="status"
          aria-live="polite"
          aria-label="Loading Tavlo"
          aria-busy={!isExiting}
        >
          <div className="relative flex flex-col items-center gap-8">
            {/* Animated logo GIF - no scale animation to prevent jitter */}
            <div
              className="relative w-[90vw] max-w-[800px] h-auto aspect-square"
              style={{ transform: "translateZ(0)" }}
            >
              <Image
                src={logoSrc}
                alt="Tavlo"
                fill
                className="object-contain"
                priority
                unoptimized
                quality={100}
                style={{ imageRendering: "auto" }}
              />
            </div>

            {/* Screen reader announcement */}
            <span className="sr-only">
              {isExiting ? "Loading complete" : "Loading Tavlo application"}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
